import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../store";
import { logout } from "../store/reducers/auth.reducer";
import { ROLES } from "../models/user.model";
import theme from "../themes";
import { ReactComponent as DashboardIcon } from "../assets/icons/sidebar/dashboard.svg";
import { ReactComponent as AgentIcon } from "../assets/icons/sidebar/agent.svg";
import { ReactComponent as InboundIcon } from "../assets/icons/sidebar/inbound.svg";
import { ReactComponent as OutboundIcon } from "../assets/icons/sidebar/outbound.svg";
import { ReactComponent as ConferenceIcon } from "../assets/icons/sidebar/conference.svg";
import { ReactComponent as ReportIcon } from "../assets/icons/sidebar/report.svg";
import { ReactComponent as SettingIcon } from "../assets/icons/sidebar/setting.svg";
import { ReactComponent as ArrowDownIcon } from "../assets/icons/arrowDown.svg";
import { useChannelSettings } from "../hooks/useChannelSettings";
import logoImage from "../assets/images/logo.svg";

interface NavSubItem {
  label: string;
  icon: React.ReactNode;
  key: string;
  to: string;
}

interface NavItemType {
  label: string;
  icon: React.ReactNode;
  key: string;
  to?: string;
  adminRequired?: boolean;
  activePrefix?: string;
  subItems?: NavSubItem[];
}

const Sidebar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();
  const { isEnabled } = useChannelSettings();

  // Get user from localStorage as fallback
  const currentUser = user || JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = currentUser.role;

  const navItems: NavItemType[] = [
    {
      label: t("navigation.dashboard"),
      icon: <DashboardIcon />,
      key: "dashboard",
      to: "/",
      adminRequired: false,
    },
    {
      label: t("navigation.agents"),
      icon: <AgentIcon />,
      key: "agents",
      to: "/agents",
    },
    {
      label: t("navigation.inbound"),
      icon: <InboundIcon />,
      key: "inbound",
      to: "/inbound",
      activePrefix: "/inbound",
      adminRequired: false,
    },
    {
      label: t("navigation.outbound"),
      icon: <OutboundIcon />,
      key: "outbound",
      to: "/outbound",
      adminRequired: false,
    },
    {
      label: t("navigation.conference"),
      icon: <ConferenceIcon />,
      key: "conference",
      to: "/conference",
      adminRequired: false,
    },
    {
      label: t("navigation.reports"),
      icon: <ReportIcon />,
      key: "reports",
      to: "/reports",
      adminRequired: false,
    },
    {
      label: t("navigation.settings"),
      icon: <SettingIcon />,
      key: "settings",
      to: "/settings",
      activePrefix: "/settings",
      adminRequired: false,
    },
  ];

  const toggleSubmenu = (key: string) => {
    setExpandedMenus((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const isSubmenuActive = (subItems: NavSubItem[]) => {
    return subItems.some((item) => {
      // Check for exact match first
      if (location.pathname === item.to) {
        return true;
      }
      // Only use startsWith if the path is longer than the item.to
      // This prevents /settings/assign-agents from matching /settings
      return location.pathname.startsWith(item.to + "/");
    });
  };

  // Filter navigation items based on user role and enabled channels
  const filteredNavItems = navItems.filter((item) => {
    if (item.adminRequired) {
      // Show admin-required items only if user role is not VIEW_ONLY (2)
      return userRole !== ROLES.VIEW_ONLY;
    }
    if (item.key === "inbound") {
      return (
        isEnabled("call") ||
        isEnabled("sms") ||
        isEnabled("whatsapp") ||
        isEnabled("email")
      );
    }
    if (item.key === "outbound") {
      return (
        isEnabled("call") ||
        isEnabled("sms") ||
        isEnabled("whatsapp") ||
        isEnabled("email")
      );
    }
    return true;
  });

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <SidebarContainer>
      <Logo>
        <LogoImage src={logoImage} alt="Lytte.ai Logo" />
        <LogoText>Lytte.ai</LogoText>
      </Logo>
      <Nav>
        {filteredNavItems.map((item) =>
          item.subItems ? (
            <SubMenuContainer key={item.key}>
              <NavItemButton
                onClick={() => toggleSubmenu(item.key)}
                $selected={isSubmenuActive(item.subItems)}
                $expanded={expandedMenus.includes(item.key)}
              >
                <IconWrapper>{item.icon}</IconWrapper>
                {item.label}
                <ArrowIcon $expanded={expandedMenus.includes(item.key)}>
                  <ArrowDownIcon />
                </ArrowIcon>
              </NavItemButton>
              {expandedMenus.includes(item.key) && (
                <SubMenuList>
                  {item.subItems.map((subItem) => {
                    // Check if this sub-item should be active
                    const isExactMatch = location.pathname === subItem.to;
                    const isChildRoute = location.pathname.startsWith(
                      subItem.to + "/"
                    );
                    const shouldBeActive =
                      subItem.to === "/settings"
                        ? isExactMatch
                        : isExactMatch || isChildRoute;

                    return (
                      <NavLink
                        key={subItem.key}
                        to={subItem.to}
                        style={{ textDecoration: "none" }}
                      >
                        <SubNavItem selected={shouldBeActive}>
                          <IconWrapper>{subItem.icon}</IconWrapper>
                          {subItem.label}
                        </SubNavItem>
                      </NavLink>
                    );
                  })}
                </SubMenuList>
              )}
            </SubMenuContainer>
          ) : (
            <NavItem
              key={item.key}
              type="button"
              selected={
                item.activePrefix
                  ? location.pathname.startsWith(item.activePrefix)
                  : item.to === "/"
                    ? location.pathname === "/"
                    : Boolean(
                        item.to &&
                          (location.pathname === item.to ||
                            location.pathname.startsWith(`${item.to}/`))
                      )
              }
              onClick={() => {
                if (item.to) navigate(item.to);
              }}
            >
              <IconWrapper>{item.icon}</IconWrapper>
              {item.label}
            </NavItem>
          )
        )}
      </Nav>
      <UserMenuContainer ref={menuRef}>
        <Avatar
          style={{ cursor: "pointer" }}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {currentUser.full_name?.charAt(0)}
        </Avatar>
        <UserName>{currentUser.full_name}</UserName>
        <UserDropdown onClick={() => setMenuOpen((v) => !v)}>
          <ArrowDownIcon />
        </UserDropdown>
        {menuOpen && (
          <DropdownMenu>
            <DropdownItem onClick={() => navigate("/profile")}>
              {t("navigation.profile")}
            </DropdownItem>
            {userRole === ROLES.ADMIN && (
              <DropdownItem onClick={() => navigate("/user-management")}>
                {t("navigation.userManagement")}
              </DropdownItem>
            )}
            <DropdownItem onClick={handleLogout}>
              {t("auth.logout")}
            </DropdownItem>
          </DropdownMenu>
        )}
      </UserMenuContainer>
    </SidebarContainer>
  );
};

const SidebarContainer = styled.div`
  width: 240px;
  max-width: 100%;
  background: #fff;
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${theme.colors.grey2};
  padding: 32px 0;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
  overflow: hidden;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 32px 64px 32px;
  flex-shrink: 0;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const LogoText = styled.h1`
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
  margin: 0;
`;

const Nav = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 12px;
  overflow-y: auto;
  overflow-x: hidden;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d0d0;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #b0b0b0;
  }
`;

const NavItem = styled.button<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ selected }) =>
    selected ? theme.colors.green2 : "transparent"};
  color: ${({ selected }) => (selected ? theme.colors.green1 : "#000")};
  font-size: 18px;
  font-weight: ${({ selected }) => (selected ? 500 : 400)};
  padding: 15px 20px;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  border: none;
  text-align: left;
  font-family: inherit;

  svg {
    fill: ${({ selected }) =>
      selected ? theme.colors.green1 : "#000"} !important;
  }

  svg path {
    fill: ${({ selected }) =>
      selected ? theme.colors.green1 : "#000"} !important;
  }

  &:hover {
    background: #e8f6ef;
  }
`;

const SubMenuContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const NavItemButton = styled.button<{
  $selected?: boolean;
  $expanded?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ $selected }) =>
    $selected ? theme.colors.green2 : "transparent"};
  color: ${({ $selected }) => ($selected ? theme.colors.green1 : "#000")};
  font-size: 18px;
  font-weight: ${({ $selected }) => ($selected ? 500 : 400)};
  padding: 15px 20px;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  border: none;
  text-align: left;

  svg {
    fill: ${({ $selected }) =>
      $selected ? theme.colors.green1 : "#000"} !important;
  }

  svg path {
    fill: ${({ $selected }) =>
      $selected ? theme.colors.green1 : "#000"} !important;
  }

  &:hover {
    background: #e8f6ef;
  }
`;

const ArrowIcon = styled.span<{ $expanded?: boolean }>`
  margin-left: auto;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) =>
    $expanded ? "rotate(180deg)" : "rotate(0deg)"};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SubMenuList = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
  margin-top: 4px;
  gap: 8px;
`;

const SubNavItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: ${({ selected }) =>
    selected ? theme.colors.green2 : "transparent"};
  color: ${({ selected }) => (selected ? theme.colors.green1 : "#666")};
  font-size: 16px;
  font-weight: ${({ selected }) => (selected ? 500 : 400)};
  padding: 12px 18px;
  border-radius: 10px;
  cursor: pointer;
  width: 100%;

  svg path {
    fill: ${({ selected }) =>
      selected ? theme.colors.green1 : "#666"} !important;
  }

  &:hover {
    background: #e8f6ef;
  }
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  flex-shrink: 0;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.colors.green1};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
`;

const UserName = styled.div`
  font-size: 16px;
  line-height: 1.2;
  color: #000;
  font-weight: 400;
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserDropdown = styled.div`
  margin-left: auto;
  color: #b0b0b0;
  cursor: pointer;
`;

const UserMenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem 1rem 0rem 1rem;
  gap: 1rem;
  flex-shrink: 0;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 16px;
  left: 16px;
  bottom: 48px;
  background: #fff;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  min-width: 140px;
  z-index: 200;
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 16px;
  color: #222;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e8f6ef;
  }
`;

export default Sidebar;
